import { CustomCellRendererProps } from "@ag-grid-community/react";
import { AgentChatThreadId } from "@hex/common";
import React, { MouseEventHandler, useCallback } from "react";
import styled from "styled-components";

import { useOnClickProps } from "../../../../hooks/useOnClickProps.js";
import { ORG_ID } from "../../../../orgs.js";
import { Routes } from "../../../../route/routes.js";
import { evtModKey } from "../../../../util/Keys.js";
import { typedMemo } from "../../../../util/typedMemo.js";
import { ProjectsTableRowData } from "../../types.js";
import { RowHeaderTableCellText, TableRowProjectTypeIcon } from "../styled.js";

export interface ThreadNameCellRendererProps<DataType>
  extends CustomCellRendererProps<DataType> {
  mapDataToHex: (data: DataType) => ProjectsTableRowData;
  threadId: string;
  threadTitle: string;
}

export const ThreadNameCellRenderer = typedMemo(function ThreadNameCellRenderer<
  D,
>({ data, threadId, threadTitle }: ThreadNameCellRendererProps<D>) {
  const threadAnchorProps = useOnClickProps({
    replace: false,
    to: Routes.THREAD.getUrl({
      threadId: threadId as AgentChatThreadId,
    }),
  });

  const handleClick: MouseEventHandler<HTMLElement> = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (!threadId) {
        return;
      }

      if (evtModKey(evt)) {
        evt.preventDefault();
        evt.stopPropagation();
        window.open(
          Routes.THREAD.href(ORG_ID, false, {
            threadId: threadId as AgentChatThreadId,
          }),
          "_blank",
        );
      } else {
        threadAnchorProps.onClick(evt);
      }
    },
    [threadId, threadAnchorProps],
  );

  if (data == null) {
    return null;
  }

  return (
    <ThreadNameContainer onClick={handleClick}>
      <ThreadNameAnchor {...threadAnchorProps} onClick={handleClick}>
        <RowHeaderTableCellText $isUnknownHex={false}>
          {threadTitle}
        </RowHeaderTableCellText>
      </ThreadNameAnchor>
    </ThreadNameContainer>
  );
});

const ThreadNameContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  ${TableRowProjectTypeIcon} {
    padding-right: 8px;
  }
`;

const ThreadNameAnchor = styled.a`
  min-width: 0;
  color: inherit;
  text-decoration: none;

  :hover {
    color: inherit;
  }
`;
